import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { API_ROOT } from '../config';
import SEO from '@/components/seo';

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  americanSilver: '#D1D1D1',
  brightSnow: '#FFFFFF',
};

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState<'form' | 'success' | 'error'>(token ? 'form' : 'error');
  const [successMessage, setSuccessMessage] = useState('');

  if (!token && status !== 'error') {
    setStatus('error');
    setError('Invalid password reset link.');
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      setIsLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      setIsLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('token', token!);
      formData.append('new_password', newPassword);

      const res = await fetch(`${API_ROOT}/reset-password`, {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setStatus('success');
        setSuccessMessage(data.message || 'Password reset successfully!');
      } else {
        setError(data.detail || 'Password reset failed. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: PALETTE.brightSnow }}>
      <SEO
        title="Reset Password"
        description="Reset your BrandComply account password securely."
        path="/reset-password"
      />
      <Card className="max-w-md w-full p-8 text-center">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <img src="/brandcomply_logo.png" alt="BrandComply" className="h-6 w-auto" />
          <span className="text-xl font-bold" style={{ color: PALETTE.inchworm }}>BrandComply</span>
        </div>

        {status === 'form' && (
          <>
            <h2 className="text-2xl font-bold mb-2" style={{ color: PALETTE.gunmetal }}>
              Set New Password
            </h2>
            <p className="text-base mb-6" style={{ color: `${PALETTE.gunmetal}80` }}>
              Enter your new password below.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4 text-left">
              <div>
                <label className="text-sm font-medium block mb-2" style={{ color: PALETTE.gunmetal }}>
                  New Password
                </label>
                <Input
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-12 border-2 rounded-xl transition-all duration-200"
                  style={{ borderColor: `${PALETTE.americanSilver}60`, background: PALETTE.brightSnow }}
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-2" style={{ color: PALETTE.gunmetal }}>
                  Confirm Password
                </label>
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-12 border-2 rounded-xl transition-all duration-200"
                  style={{ borderColor: `${PALETTE.americanSilver}60`, background: PALETTE.brightSnow }}
                />
              </div>

              {error && (
                <div 
                  className="text-sm text-center p-3 rounded-lg" 
                  style={{ 
                    color: PALETTE.orange,
                    background: `${PALETTE.orange}10`,
                    border: `1px solid ${PALETTE.orange}20`
                  }}
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-12 rounded-full font-semibold text-base transition-all duration-200 hover:scale-105"
                style={{ 
                  background: `linear-gradient(135deg, ${PALETTE.inchworm}, ${PALETTE.inchworm}e6)`,
                  color: PALETTE.gunmetal,
                  border: 'none',
                  borderRadius: '9999px'
                }}
                disabled={isLoading}
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mx-auto mb-4 w-12 h-12 rounded-full flex items-center justify-center" style={{ background: `${PALETTE.inchworm}30` }}>
              <svg className="w-6 h-6" fill="none" stroke={PALETTE.gunmetal} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: PALETTE.gunmetal }}>
              Password Reset!
            </h2>
            <p className="text-base mb-6" style={{ color: `${PALETTE.gunmetal}80` }}>
              {successMessage}
            </p>
            <Button
              onClick={() => navigate('/')}
              className="w-full h-12 rounded-full font-semibold text-base transition-all duration-200 hover:scale-105"
              style={{ 
                background: `linear-gradient(135deg, ${PALETTE.inchworm}, ${PALETTE.inchworm}e6)`,
                color: PALETTE.gunmetal,
                border: 'none',
                borderRadius: '9999px'
              }}
            >
              Go to Sign In
            </Button>
          </>
        )}

        {status === 'error' && !token && (
          <>
            <div className="mx-auto mb-4 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: PALETTE.gunmetal }}>
              Invalid Link
            </h2>
            <p className="text-base mb-6" style={{ color: `${PALETTE.gunmetal}80` }}>
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="w-full h-12 rounded-full font-semibold text-base transition-all duration-200 hover:scale-105"
              style={{ 
                background: `linear-gradient(135deg, ${PALETTE.inchworm}, ${PALETTE.inchworm}e6)`,
                color: PALETTE.gunmetal,
                border: 'none',
                borderRadius: '9999px'
              }}
            >
              Go to Sign In
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
